import { useEffect, useMemo, useRef, useState } from "react";

interface ExpandableTextProps {
  children: React.ReactNode;
  /** Nếu truyền chars -> dùng chế độ cắt theo số ký tự (ưu tiên hơn lines) */
  chars?: number;
  /** Nếu không truyền chars, sẽ dùng line-clamp theo số dòng */
  lines?: number; // mặc định 1
  seeMoreText?: string;
  seeLessText?: string;
  /** Thêm class cho nút */
  toggleClassName?: string;
}

/** Walk DOM và clone đến khi đủ số ký tự, giữ cấu trúc thẻ */
function truncateDomByChars(root: HTMLElement, limit: number): string {
  let count = 0;
  let done = false;

  function walk(node: Node): Node | null {
    if (done) return null;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? "");
      if (!text) return document.createTextNode("");

      if (count + text.length <= limit) {
        count += text.length;
        return document.createTextNode(text);
      } else {
        const remain = Math.max(0, limit - count);
        const slice = text.slice(0, remain).replace(/\s+$/u, "");
        count = limit;
        done = true;
        return document.createTextNode(slice);
      }
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const clone = document.createElement(el.tagName.toLowerCase());

      // copy minimal attrs that affect style/semantics
      for (const attr of el.getAttributeNames()) {
        if (attr === "style" || attr === "class" || attr.startsWith("data-") || attr === "href" || attr === "target" || attr === "rel") {
          const val = el.getAttribute(attr);
          if (val) clone.setAttribute(attr, val);
        }
      }

      for (const child of Array.from(el.childNodes)) {
        if (done) break;
        const childClone = walk(child);
        if (childClone) clone.appendChild(childClone);
      }
      return clone;
    }

    return null;
  }

  const frag = document.createDocumentFragment();
  for (const child of Array.from(root.childNodes)) {
    if (done) break;
    const piece = walk(child);
    if (piece) frag.appendChild(piece);
  }

  // tạo container tạm để lấy innerHTML
  const div = document.createElement("div");
  div.appendChild(frag);
  return div.innerHTML;
}

export default function ExpandableText({
  children,
  chars,
  lines = 1,
  seeMoreText = "See more",
  seeLessText = "",
  toggleClassName = "ml-1 text-sm font-semibold text-gray-800 hover:underline"
}: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  // refs cho 2 chế độ
  const hiddenFullRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef<HTMLDivElement>(null);

  // Tính truncated HTML một lần khi có children/chars
  const truncatedHtml = useMemo(() => {
    if (!chars) return "";
    // tạo container offscreen tạm để đo và walk
    const temp = document.createElement("div");
    // render tạm children (text thôi) – ta sẽ mount children thật trong hidden div ở JSX để chính xác hơn
    return ""; // thực sự build trong useEffect sau khi DOM có children thật
  }, [children, chars]);

  // State chứa html cắt theo chars
  const [htmlByChars, setHtmlByChars] = useState<string>("");

  useEffect(() => {
    if (!chars) return;
    // Sau khi hiddenFullRef đã mount children thật, ta mới walk DOM để giữ cấu trúc
    const root = hiddenFullRef.current;
    if (!root) return;

    const fullTextLen = root.innerText.length;
    if (fullTextLen > chars) {
      const html = truncateDomByChars(root, chars);
      setHtmlByChars(html);
      setShowToggle(true);
    } else {
      setHtmlByChars(root.innerHTML);
      setShowToggle(false);
    }
  }, [children, chars]);


  useEffect(() => {
    if (chars) return; 
    const el = visibleRef.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight || "20");
    const maxHeight = lineHeight * lines;
    const over = el.scrollHeight > maxHeight + 1; 
    setShowToggle(over);
  }, [children, lines, chars]);

  // Render
  if (chars) {
    return (
      <>
        <div ref={hiddenFullRef} className="hidden">
          {children}
        </div>

        {/* Visible */}
        <div className="relative inline">
          <span
            className="inline"
            dangerouslySetInnerHTML={{ __html: expanded ? (hiddenFullRef.current?.innerHTML || htmlByChars) : (htmlByChars || "") }}
          />
          {showToggle && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className={toggleClassName}
            >
              {expanded ? seeLessText : "… " + seeMoreText}
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="relative">
      <div
        ref={visibleRef}
        className={`transition-all duration-300 ease-in-out ${
          expanded ? "line-clamp-none" : `line-clamp-${lines}`
        }`}
        style={!expanded ? { WebkitLineClamp: lines } : {}}
      >
        {children}
      </div>
      {showToggle && (
        <button
          onClick={() => setExpanded(!expanded)}
          className={toggleClassName}
        >
          {expanded ? seeLessText : seeMoreText}
        </button>
      )}
    </div>
  );
}
